import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { baseUrl } from '../Data/service/global.service';
import {
  TaskCompletedEvent,
  TaskMessageReceivedEvent,
  TaskParticipantAddedEvent,
  TaskReadReceiptEvent,
  TaskStatusChangedEvent,
  TaskUnreadCountUpdatedEvent,
  NotificationReadEvent,
  TaskViewedEvent,
  UserPostponementChangedEvent,
} from '../models/task.model';

@Injectable({
  providedIn: 'root',
})
export class TasksWebSocketService implements OnDestroy {
  private socket: Socket | null = null;
  private connected$ = new BehaviorSubject<boolean>(false);
  private messageReceived$ = new Subject<TaskMessageReceivedEvent>();
  private taskStatusChanged$ = new Subject<TaskStatusChangedEvent>();
  private taskCompleted$ = new Subject<TaskCompletedEvent>();
  private readReceipt$ = new Subject<TaskReadReceiptEvent>();
  private unreadCountUpdated$ = new Subject<TaskUnreadCountUpdatedEvent>();
  private notificationRead$ = new Subject<NotificationReadEvent>();
  private participantAdded$ = new Subject<TaskParticipantAddedEvent>();
  private taskViewed$ = new Subject<TaskViewedEvent>();
  private userPostponementChanged$ = new Subject<UserPostponementChangedEvent>();
  private joinedTaskIds = new Set<number>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    if (this.socket && !this.socket.connected) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    const wsUrl = baseUrl.replace(/\/$/, '');

    this.socket = io(`${wsUrl}/tasks`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: this.reconnectDelay,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    this.socket.on('connect', () => {
      this.connected$.next(true);
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.rejoinTasks();
    });

    this.socket.on('disconnect', () => {
      this.connected$.next(false);
    });

    this.socket.on('connect_error', () => {
      this.connected$.next(false);
      this.handleReconnect();
    });

    this.socket.on('message_received', (data: TaskMessageReceivedEvent) => {
      this.messageReceived$.next(data);
    });

    this.socket.on('task_status_changed', (data: TaskStatusChangedEvent) => {
      this.taskStatusChanged$.next(data);
    });

    this.socket.on('task_completed', (data: TaskCompletedEvent) => {
      this.taskCompleted$.next(data);
    });

    this.socket.on('read_receipt', (data: TaskReadReceiptEvent) => {
      this.readReceipt$.next(data);
    });

    this.socket.on('unread_count_updated', (data: TaskUnreadCountUpdatedEvent) => {
      this.unreadCountUpdated$.next(data);
    });

    this.socket.on('notification_read', (data: NotificationReadEvent) => {
      this.notificationRead$.next(data);
    });

    this.socket.on('participant_added', (data: TaskParticipantAddedEvent) => {
      this.participantAdded$.next(data);
    });

    this.socket.on('task_viewed', (data: TaskViewedEvent) => {
      this.taskViewed$.next(data);
    });

    this.socket.on('user_postponement_changed', (data: UserPostponementChangedEvent) => {
      this.userPostponementChanged$.next(data);
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected$.next(false);
    }
  }

  joinTask(taskId: number): void {
    if (!taskId) {
      return;
    }
    this.joinedTaskIds.add(taskId);
    if (this.socket?.connected) {
      this.socket.emit('join_task', { task_id: taskId });
    }
  }

  leaveTask(taskId: number): void {
    if (!taskId) {
      return;
    }
    this.joinedTaskIds.delete(taskId);
    if (this.socket?.connected) {
      this.socket.emit('leave_task', { task_id: taskId });
    }
  }

  getConnectionStatus(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  onMessageReceived(): Observable<TaskMessageReceivedEvent> {
    return this.messageReceived$.asObservable();
  }

  onTaskStatusChanged(): Observable<TaskStatusChangedEvent> {
    return this.taskStatusChanged$.asObservable();
  }

  onTaskCompleted(): Observable<TaskCompletedEvent> {
    return this.taskCompleted$.asObservable();
  }

  onReadReceipt(): Observable<TaskReadReceiptEvent> {
    return this.readReceipt$.asObservable();
  }

  onUnreadCountUpdated(): Observable<TaskUnreadCountUpdatedEvent> {
    return this.unreadCountUpdated$.asObservable();
  }

  onNotificationRead(): Observable<NotificationReadEvent> {
    return this.notificationRead$.asObservable();
  }

  onParticipantAdded(): Observable<TaskParticipantAddedEvent> {
    return this.participantAdded$.asObservable();
  }

  onTaskViewed(): Observable<TaskViewedEvent> {
    return this.taskViewed$.asObservable();
  }

  onUserPostponementChanged(): Observable<UserPostponementChangedEvent> {
    return this.userPostponementChanged$.asObservable();
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  private rejoinTasks(): void {
    if (!this.socket?.connected) {
      return;
    }
    this.joinedTaskIds.forEach((taskId) => {
      this.socket!.emit('join_task', { task_id: taskId });
    });
  }

  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }
    this.reconnectAttempts++;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
    setTimeout(() => {
      if (!this.socket?.connected) {
        this.connect();
      }
    }, this.reconnectDelay);
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.messageReceived$.complete();
    this.taskStatusChanged$.complete();
    this.taskCompleted$.complete();
    this.readReceipt$.complete();
    this.unreadCountUpdated$.complete();
    this.notificationRead$.complete();
    this.participantAdded$.complete();
    this.taskViewed$.complete();
    this.userPostponementChanged$.complete();
    this.connected$.complete();
  }
}
